import * as React from 'react';
import { Box, Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Typography from '@mui/material/Typography';
import FileIcon from '@mui/icons-material/DescriptionOutlined';
import MyImageGallery from '../global/ImageGallery';
import { FileSectionRow } from '../../interfaces';

type Props = {
  label: string;
  values: FileSectionRow['value'];
};

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
    // eslint-disable-next-line react/jsx-props-no-spreading
  ) => <Slide direction="up" ref={ref} {...props} />,
);

export default function SectionFileRow({ label, values }: Props) {
  const [galleryOpen, setGalleryOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(0);

  const images = values.filter(file => file.type === 'image');

  const handleClickOpen = (index: number) => {
    const file = values[index];

    if (file.type === 'pdf') {
      window.open(file.url, '_blank');
      return;
    }

    setGalleryOpen(true);
    const imageIndex = images.findIndex(image => image.url === file.url);
    setCurrentImageIndex(imageIndex);
  };

  const handleClose = () => {
    setGalleryOpen(false);
  };

  return (
    <Box className="section-row" width="100%">
      <Typography variant="h4">{label}</Typography>
      <ImageList cols={3} rowHeight={164} sx={{ position: 'relative' }}>
        {values.map((file, index) => (
          <ImageListItem
            key={file.url + file.type + file.name}
            sx={{
              position: 'relative',
              borderRadius: '10px',
              bgcolor: theme => theme.palette.lightGrey.main,
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={() => handleClickOpen(index)}>
            {file.type === 'image' && (
              <img
                src={file.url}
                alt="img"
                style={{
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%',
                }}
              />
            )}
            {file.type === 'pdf' && (
              <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center">
                <FileIcon
                  sx={{
                    fontSize: '3rem',
                  }}
                />
              </Box>
            )}
            {file.actionComponent && (
              <Box
                position="absolute"
                top={theme => theme.spacing(1)}
                right={theme => theme.spacing(1)}>
                {file.actionComponent}
              </Box>
            )}
            {file.showName && (
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                p={1}
                bgcolor="rgba(0, 0, 0, 0.5)">
                <Typography
                  title={file.name}
                  variant="caption"
                  display="block"
                  color="white"
                  textAlign="center"
                  fontWeight="bold"
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}>
                  {file.name}
                </Typography>
              </Box>
            )}
          </ImageListItem>
        ))}
      </ImageList>
      <Dialog
        fullScreen
        open={galleryOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        className="gallery-modal">
        <MyImageGallery
          images={images.map(file => file.url)}
          closeGallery={handleClose}
          currentIndex={currentImageIndex}
        />
      </Dialog>
    </Box>
  );
}
